// react
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
// mantine
import { ActionIcon, Menu } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
// icons
import { Plus, EllipsisVertical } from 'lucide-react';
// components
import type { TAgentWithFlows } from '@newo/api/gateway';
import AgentForm from '@/pages/Agents/components/AgentForm';
import AgentsGrid from '@/pages/Agents/components/AgentsGrid';
import Drawer from '@/components/Drawer';
// utils
import { QUERY_KEYS } from '@/services/bff/bff.queries.ts';

interface AgentsProps {
    projectId?: string;
}

const Agents = ({ projectId }: AgentsProps) => {
    const [selectedAgent, setSelectedAgent] = useState<
        TAgentWithFlows | undefined
    >();
    const [isDrawerOpened, handlers] = useDisclosure(false);
    const queryClient = useQueryClient();

    const handleNewAgent = () => {
        queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.getAgentsList()
        });
    };

    const handleAgentDrawerOpen = (agent?: TAgentWithFlows) => {
        setSelectedAgent(agent);
        handlers.open();
    };

    const handleAgentDrawerClose = () => {
        setSelectedAgent(undefined);
        handlers.close();
    };

    return (
        <div className="w-full">
            <div className="flex gap-4 justify-end py-4">
                <Menu>
                    <Menu.Target>
                        <ActionIcon variant="transparent">
                            <EllipsisVertical />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item
                            leftSection={<Plus />}
                            onClick={() => {
                                handleAgentDrawerOpen();
                            }}
                        >
                            Create Agent
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </div>
            <AgentsGrid
                projectId={projectId}
                onAgentEdit={handleAgentDrawerOpen}
            />
            <Drawer
                withOverlay
                name="agent-form"
                opened={isDrawerOpened}
                position="right"
                size="sm"
                title={
                    selectedAgent
                        ? `Edit ${selectedAgent.idn} agent`
                        : 'Create Agent'
                }
                onClose={handleAgentDrawerClose}
            >
                <AgentForm
                    agent={selectedAgent}
                    projectId={projectId}
                    onClose={handleAgentDrawerClose}
                    onSubmit={handleNewAgent}
                />
            </Drawer>
        </div>
    );
};

export default Agents;
